/*
 * Lightweight JWT helper for the dashboard.
 *
 * Phase 3 placeholder: the seller-auth flow is being designed against
 * auth.request.network (plan G9 spike). Until that lands, the dashboard
 * stores a hand-pasted JWT in localStorage and treats its presence as
 * "authenticated". Real session lifecycle (refresh, expiry, logout) is a
 * follow-up PR — JWT_SIGNING_KEY is owned by apps/api and rotated there.
 */

const TOKEN_KEY = 'caratuva.jwt';

export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(TOKEN_KEY);
}

// Decode the `exp` claim (seconds since epoch) from the stored JWT. Returns
// null when there is no token or the claim is missing/non-numeric.
export function getTokenExpiry(): number | null {
  const token = getToken();
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      exp?: number;
    };
    return typeof json.exp === 'number' ? json.exp : null;
  } catch {
    return null;
  }
}

// True only when a token exists AND its exp claim is in the past. A missing
// token is treated as "not expired" (there's simply nothing to expire).
export function isTokenExpired(): boolean {
  const exp = getTokenExpiry();
  if (exp === null) return false;
  return exp * 1000 <= Date.now();
}

// Expiry is enforced client-side here so the UI can react immediately (see
// _app.tsx's beforeLoad + idle-tab interval); the API's 401 response on an
// expired JWT remains the server-side backstop (see lib/api.ts).
export function isAuthenticated(): boolean {
  return Boolean(getToken()) && !isTokenExpired();
}

export type OrgMode = 'test' | 'live';
export type UserRole = 'admin' | 'treasurer' | 'viewer' | 'ops';

// Decode the `role` claim from the stored JWT. Used only for client-side UX
// gating (showing the ops-only admin surface) — the real authorization is the
// server-side @OpsOnly() guard, which rejects non-ops principals regardless.
export function getRole(): UserRole | null {
  const token = getToken();
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      role?: string;
    };
    if (
      json.role === 'admin' ||
      json.role === 'treasurer' ||
      json.role === 'viewer' ||
      json.role === 'ops'
    ) {
      return json.role;
    }
    return null;
  } catch {
    return null;
  }
}

// Decode the `mode` claim from the stored JWT. The token is the single source
// of truth for the active mode (set on consume, re-minted by switch-mode), so
// we read it back rather than tracking mode separately. Defaults to `test`.
export function getMode(): OrgMode {
  const token = getToken();
  if (!token) return 'test';
  try {
    const payload = token.split('.')[1];
    if (!payload) return 'test';
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      mode?: string;
    };
    return json.mode === 'live' ? 'live' : 'test';
  } catch {
    return 'test';
  }
}
